import { giphyUrl } from '@/app/util/env'
import { giphyDarkestGrey } from '@giphy/colors'
import { setGADataLayer } from 'analytics'
import { useContext, useReducer, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import ReCAPTCHA from 'react-google-recaptcha'
import useLocalStorage from 'react-use/lib/useLocalStorage'
import styled from 'styled-components'
import type { ILoggedInUser } from 'types'
import Checkbox from 'ui/src/components/controls/checkbox'
import { getErrorMessages } from 'utils/src/api'
import { timeoutFetch } from 'utils/src/api/fetch'
import { FlashMessageContext } from '../../context/flash-message'
import { appleLogo, fbLogo } from '../../util/asset-url'
import EmailValidation, { defaultEmailValidationCopy, type EmailValidationCopy } from './email-validation'
import AuthModal, { defaultAccountRecoveryCopy, type AccountRecoveryCopy } from './modal'
import PasswordVisibility from './password-visibility'
import { Button, Form, SocButton, SocialButtons, Terms } from './sc'
import { useSocialAuthPopup } from './use-social-auth-popup'

const SignUpRecovery = styled.p`
    font-size: 14px;
    text-align: center;
`
const SignUpRecoveryLink = styled.a`
    font-weight: bold;
    cursor: pointer;
`

const TermsCheckbox = styled(Checkbox)`
    display: inline-block;
    margin-right: 6px;
    vertical-align: top;
`

const ReCAPTCHAContainer = styled.div`
    min-height: 74px;
    width: 325px;
    display: flex;
    justify-content: center;
`

export type JoinFormCopy = {
    accountRecovery: AccountRecoveryCopy
    accountRecoveryCta: string
    accountRecoveryPrompt: string
    appleAlt: string
    appleSignUp: string
    confirmPasswordPlaceholder: string
    emailPlaceholder: string
    emailValidation: EmailValidationCopy
    errors: {
        multiple: string
    }
    facebookSignUp: string
    passwordPlaceholder: string
    passwordVisibility: {
        hide: string
        show: string
    }
    socialAuth: {
        checkLogin: string
        finishedPrompt: string
        pending: string
    }
    submit: string
    terms: {
        checkboxTitle: string
        conjunction: string
        prefix: string
        privacyPolicy: string
        termsOfService: string
    }
    usernamePlaceholder: string
}

export const defaultJoinFormCopy: JoinFormCopy = {
    accountRecovery: defaultAccountRecoveryCopy,
    accountRecoveryCta: 'Finish Sign Up',
    accountRecoveryPrompt: 'Already started creating an account?',
    appleAlt: 'apple login',
    appleSignUp: 'Sign up with Apple',
    confirmPasswordPlaceholder: 'Confirm your password',
    emailPlaceholder: 'Email Address',
    emailValidation: defaultEmailValidationCopy,
    errors: {
        multiple: 'Please try again after fixing the following errors:',
    },
    facebookSignUp: 'Sign up with Facebook',
    passwordPlaceholder: 'Password',
    passwordVisibility: {
        hide: 'Hide password.',
        show: 'Show password as plain text. Warning: this will display your password on the screen.',
    },
    socialAuth: {
        checkLogin: 'Check login',
        finishedPrompt: 'Finished signing up?',
        pending: 'Complete sign up in the popup window.',
    },
    submit: 'Sign Up',
    terms: {
        checkboxTitle: 'Terms of Service',
        conjunction: 'and',
        prefix: 'I agree to the',
        privacyPolicy: 'Privacy Policy',
        termsOfService: 'Terms of Service',
    },
    usernamePlaceholder: 'Username',
}

const JoinForm = ({
    copy = defaultJoinFormCopy,
    mode = 'page',
    onSuccess,
    onSwitchToLogin,
}: {
    copy?: JoinFormCopy
    mode?: 'modal' | 'page'
    next?: string
    onSuccess?: (user: ILoggedInUser) => void | Promise<void>
    onSwitchToLogin?: (() => void) | undefined
}) => {
    const form = useRef<HTMLFormElement>(null)
    const emailInput = useRef<HTMLInputElement>(null)
    const userInput = useRef<HTMLInputElement>(null)
    const passwordInput = useRef<HTMLInputElement>(null)
    const [viewPassword, setViewPassword] = useState(false)
    const passwordInput2 = useRef<HTMLInputElement>(null)
    const [viewPassword2, setViewPassword2] = useState(false)
    const captcha = useRef<ReCAPTCHA>(null)
    const passwordsMatch = passwordInput.current?.value === passwordInput2.current?.value
    const [, forceUpdate] = useReducer((x) => x + 1, 0)
    const [terms, setTerms] = useState(false)
    const [showRecoveryModal, setShowRecoveryModal] = useState(false)
    const [emailValidation, setEmailValidation] = useState({
        show: false,
        email: '',
    })
    const [loading, setLoading] = useState(false)
    const { showMessage } = useContext(FlashMessageContext)
    const facebookAuthHref = `${giphyUrl}/login/facebook/`
    const appleAuthHref = `${giphyUrl}/login/apple/`
    const { completeSocialAuth, openSocialAuthPopup, socialAuthState } = useSocialAuthPopup({ mode, onSuccess })
    const socialAuthPending = socialAuthState.status === 'pending'

    const [_, setLoginMethod] = useLocalStorage('login_type')
    const submit = async () => {
        const formData = new FormData(form.current!)
        formData.append('platform', 'web')
        formData.append('recaptcha_token', captcha.current!.getValue()!)
        try {
            setLoading(true)
            const response = await timeoutFetch({
                url: '/api/v3/register',
                options: {
                    method: 'POST',
                    body: formData,
                },
            })
            if (response.ok) {
                setLoginMethod('email')
                setGADataLayer({
                    event: 'signup_form_success',
                    options: {
                        form: {
                            formName: 'signup',
                        },
                    },
                })
                setEmailValidation({
                    show: true,
                    email: emailInput.current!.value,
                })
            } else {
                const errorMessages = getErrorMessages(await response.json())
                if (errorMessages.length > 1) {
                    showMessage({
                        message: (
                            <>
                                <div>{copy.errors.multiple}</div>
                                {errorMessages.map((mes, index) => {
                                    return (
                                        <div key={index}>
                                            {index + 1}. {mes}
                                        </div>
                                    )
                                })}
                            </>
                        ),
                        type: 'error',
                    })
                } else {
                    showMessage({
                        message: errorMessages.map((mes, index) => <div key={index}>{mes}</div>),
                        type: 'error',
                    })
                }
                captcha.current?.reset()
                setLoading(false)
                // Send GTM error
                setGADataLayer({
                    event: 'signup_form_error',
                    options: {
                        form: {
                            formName: 'signup',
                            formError: errorMessages.join(','),
                        },
                    },
                })
            }
        } catch (err: any) {}
    }
    return (
        <>
            <SignUpRecovery>
                {copy.accountRecoveryPrompt}{' '}
                <SignUpRecoveryLink onClick={() => setShowRecoveryModal(true)} type="button">
                    {copy.accountRecoveryCta}
                </SignUpRecoveryLink>
            </SignUpRecovery>
            <Form ref={form} noValidate>
                <div>
                    <input
                        autoComplete="username"
                        placeholder={copy.emailPlaceholder}
                        name="email"
                        required
                        type="email"
                        ref={emailInput}
                        onKeyUp={forceUpdate}
                        onInput={forceUpdate}
                    />
                    <div className="error-message">{emailInput.current?.validationMessage}</div>
                </div>
                <div>
                    <input
                        placeholder={copy.usernamePlaceholder}
                        name="username"
                        required
                        type="text"
                        autoComplete="off"
                        ref={userInput}
                        onKeyUp={forceUpdate}
                        onInput={forceUpdate}
                    />
                    <div className="error-message">{userInput.current?.validationMessage}</div>
                </div>
                <div style={{ position: 'relative' }}>
                    <input
                        autoComplete="new-password"
                        name="password"
                        placeholder={copy.passwordPlaceholder}
                        required
                        type={viewPassword ? 'text' : 'password'}
                        minLength={8}
                        onInput={forceUpdate}
                        onKeyUp={forceUpdate}
                        ref={passwordInput}
                    />
                    <PasswordVisibility
                        ariaLabel={viewPassword ? copy.passwordVisibility.hide : copy.passwordVisibility.show}
                        viewPassword={viewPassword}
                        setViewPassword={setViewPassword}
                    />
                    <div className="error-message">{passwordInput.current?.validationMessage}</div>
                </div>
                <div style={{ position: 'relative' }}>
                    <input
                        autoComplete="new-password"
                        name="password"
                        placeholder={copy.confirmPasswordPlaceholder}
                        required
                        type={viewPassword2 ? 'text' : 'password'}
                        minLength={8}
                        onKeyUp={forceUpdate}
                        onInput={forceUpdate}
                        ref={passwordInput2}
                    />
                    <PasswordVisibility
                        ariaLabel={viewPassword2 ? copy.passwordVisibility.hide : copy.passwordVisibility.show}
                        viewPassword={viewPassword2}
                        setViewPassword={setViewPassword2}
                    />
                    <div className="error-message">{passwordInput.current?.validationMessage}</div>
                </div>
                <ReCAPTCHAContainer className="ci-removed">
                    <ReCAPTCHA
                        sitekey="6LdLAasUAAAAAJIGrMFJ9wvf1UXIibLjHyeCZgH3"
                        ref={captcha}
                        onInput={forceUpdate}
                        onChange={forceUpdate}
                    />
                </ReCAPTCHAContainer>
                <Terms>
                    <TermsCheckbox
                        checked={terms}
                        name="Terms"
                        size="small"
                        title={copy.terms.checkboxTitle}
                        onChange={(isChecked: boolean) => {
                            setTerms(isChecked)
                        }}
                    />
                    {copy.terms.prefix} <a href={`/terms`}>{copy.terms.termsOfService}</a> {copy.terms.conjunction}{' '}
                    <a href="https://support.giphy.com/hc/en-us/articles/360032872931">{copy.terms.privacyPolicy}</a>
                </Terms>
                <Button
                    size="medium"
                    gradient="purple-indigo"
                    loading={loading}
                    disabled={
                        !form.current?.checkValidity() || !passwordsMatch || !terms || !captcha.current?.getValue()
                    }
                    onClick={submit}
                >
                    {copy.submit}
                </Button>
            </Form>
            <SocialButtons>
                <Button
                    size="medium"
                    disabled={socialAuthPending}
                    href={mode === 'modal' ? undefined : facebookAuthHref}
                    onClick={mode === 'modal' ? () => openSocialAuthPopup('facebook', facebookAuthHref) : undefined}
                    color={giphyDarkestGrey}
                >
                    <SocButton>
                        {}
                        <img src={fbLogo} alt="Facebook" style={{ margin: -6 }} />
                        {copy.facebookSignUp}
                    </SocButton>
                </Button>
                <Button
                    size="medium"
                    disabled={socialAuthPending}
                    href={mode === 'modal' ? undefined : appleAuthHref}
                    onClick={mode === 'modal' ? () => openSocialAuthPopup('apple', appleAuthHref) : undefined}
                    color={giphyDarkestGrey}
                >
                    <SocButton>
                        {}
                        <img src={appleLogo} alt={copy.appleAlt} height="16" width="16" />
                        {copy.appleSignUp}
                    </SocButton>
                </Button>
            </SocialButtons>
            {mode === 'modal' && socialAuthState.status !== 'idle' && (
                <div className="text-center text-xs leading-snug font-semibold text-giphyWhite">
                    {socialAuthState.status === 'pending' ? (
                        copy.socialAuth.pending
                    ) : (
                        <>
                            {copy.socialAuth.finishedPrompt}{' '}
                            <button
                                className="cursor-pointer border-0 bg-transparent p-0 font-bold text-giphyBlue hover:text-giphyWhite"
                                type="button"
                                onClick={completeSocialAuth}
                            >
                                {copy.socialAuth.checkLogin}
                            </button>
                        </>
                    )}
                </div>
            )}
            {showRecoveryModal &&
                createPortal(
                    <AuthModal
                        copy={copy.accountRecovery}
                        onClose={(email?: string) => {
                            setShowRecoveryModal(false)
                            if (email) {
                                setEmailValidation({ show: true, email })
                            }
                        }}
                    />,
                    document.body,
                    'recovery-modal'
                )}
            {emailValidation.show &&
                createPortal(
                    <EmailValidation
                        copy={copy.emailValidation}
                        email={emailValidation.email}
                        loginHref={mode === 'modal' ? undefined : '/login'}
                        onVerified={mode === 'modal' ? onSwitchToLogin : undefined}
                    />,
                    document.body,
                    'email-validation'
                )}
        </>
    )
}
export default JoinForm
